// src/pages/AdminDashboard.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchUserDetails } from "../actions/AuthActions";
import { fetchUsers as fetchUsersList } from "../actions/UserActions";
import { toast } from "react-toastify";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Truck,
  MapPin,
  Camera,
  Edit2,
  Save,
  X,
  Eye,
  EyeOff,
  Users,
  Activity,
  Shield,
  ChevronRight,
  FileText,
  UserPlus,
  Car,
  ClipboardList,
  Settings,
  UserCog,
  UserMinus,
} from "lucide-react";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import { getCroppedImg } from "../utils/cropImage";
import AdminLayout from "../components/AdminLayout";

// Vehicle icons
import CarIcon from "../assets/car.png";
import VanIcon from "../assets/van.png";
import BusIcon from "../assets/bus.png";
import SuvIcon from "../assets/suv.png";
import CabIcon from "../assets/cab.png";
import ForkliftIcon from "../assets/fork lift.png";
import LorryIcon from "../assets/lorry.png";
import CherryIcon from "../assets/cherry.png";
import CraneIcon from "../assets/crane.jpg";

const TYPE_ICONS = {
  C: CarIcon,
  V: VanIcon,
  B: BusIcon,
  S: SuvIcon,
  CA: CabIcon,
  F: ForkliftIcon,
  L: LorryIcon,
  CH: CherryIcon,
  T: LorryIcon,
  CR: CraneIcon,
};

// Fix Leaflet default marker issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png").default,
  iconUrl: require("leaflet/dist/images/marker-icon.png").default,
  shadowUrl: require("leaflet/dist/images/marker-shadow.png").default,
});

// Stats card color configurations
const statsConfig = {
  totalUsers: {
    bg: "bg-purple-50",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    border: "border-purple-200"
  },
  activeUsers: {
    bg: "bg-emerald-50",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    border: "border-emerald-200"
  },
  adminAccounts: {
    bg: "bg-blue-50",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    border: "border-blue-200"
  },
  inactiveUsers: {
    bg: "bg-gray-50",
    iconBg: "bg-gray-100",
    iconColor: "text-gray-600",
    border: "border-gray-200"
  },
};

const AdminDashboard = () => {
  const { userDetails, authKey } = useSelector((state) => state.auth);
  const { items: users, loading } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  // ===== Profile Edit State =====
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    Name: "",
    MobileNo: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  // ===== Profile Image Update =====
  const [previewImage, setPreviewImage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [profileImageBlob, setProfileImageBlob] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);

  // Update current time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch all users
  useEffect(() => {
    if (authKey) {
      dispatch(fetchUsersList());
    }
  }, [authKey, dispatch]);

  // Initialize edit form data
  useEffect(() => {
    if (userDetails && !isEditing) {
      setEditFormData({
        Name: userDetails.Name || "",
        MobileNo: userDetails.MobileNo || "",
        password: userDetails.password || "",
      });
    }
  }, [userDetails, isEditing]);

  // Quick stats
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.status === "Active").length;
  const inactiveUsers = totalUsers - activeUsers;
  const adminAccounts = users.filter((u) => u.role === "Admin").length;

  // Profile image url
  const profileImageUrl = previewImage || (userDetails?.ServiceNo
    ? `https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${userDetails.ServiceNo}&t=${Date.now()}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(userDetails?.Name || "Admin")}&background=111827&color=fff&size=96&bold=true`
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile(file);
      setPreviewImage(reader.result);
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const applyCrop = useCallback(async () => {
    if (!croppedAreaPixels || !previewImage) return;
    try {
      const blob = await getCroppedImg(previewImage, croppedAreaPixels);
      setProfileImageBlob(blob);
      setPreviewImage(URL.createObjectURL(blob));
      setShowCropModal(false);
      setSelectedFile(null);
    } catch (err) {
      console.error(err);
    }
  }, [croppedAreaPixels, previewImage]);

  const cancelCrop = () => {
    setPreviewImage(`https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${userDetails?.ServiceNo}&t=${Date.now()}`);
    setSelectedFile(null);
    setProfileImageBlob(null);
    setShowCropModal(false);
  };

  const uploadImageToServer = async (blobOrFile) => {
    const file = blobOrFile instanceof File ? blobOrFile : new File([blobOrFile], `${userDetails.ServiceNo}.jpg`, { type: "image/jpeg" });
    const fd = new FormData();
    fd.append("ServiceNo", userDetails.ServiceNo);
    fd.append("ImageFile", file);

    const uploadUrl = "https://aeroviewbackend.dockyardsoftware.com/User/Fileupload";
    const resp = await fetch(uploadUrl, {
      method: "POST",
      body: fd,
      headers: { "auth-key": authKey }
    });
    if (!resp.ok) throw new Error("Upload failed");
    toast.success("Profile image updated");
    setProfileImageBlob(null);
    setPreviewImage(`https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${userDetails?.ServiceNo}&t=${Date.now()}`);
  };

  const handleSaveProfileImage = async () => {
    if (profileImageBlob) {
      await uploadImageToServer(profileImageBlob);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "MobileNo") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    if (name === "Name" && !/^[a-zA-Z\s]*$/.test(value)) {
      return;
    }

    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateEditForm = () => {
    if (!editFormData.Name.trim()) {
      toast.error("Name required");
      return false;
    }
    if (editFormData.Name.trim().length < 3) {
      toast.error("Minimum 3 characters");
      return false;
    }
    if (!editFormData.MobileNo.trim()) {
      toast.error("Mobile number required");
      return false;
    }
    if (!/^\d{10}$/.test(editFormData.MobileNo)) {
      toast.error("10 digits required");
      return false;
    }
    if (!editFormData.password) {
      toast.error("Password required");
      return false;
    }
    if (editFormData.password.length < 8) {
      toast.error("Minimum 8 characters");
      return false;
    }
    return true;
  };

  const handleUpdateProfile = async () => {
    if (!validateEditForm()) return;

    try {
      const mapRoleToCode = (roleName) => {
        if (!roleName) return "E";
        const r = roleName.toString().toUpperCase();
        if (r === "ADMIN" || r === "A") return "A";
        if (r === "EMPLOYEE" || r === "E") return "E";
        if (r === "SUPERVISOR" || r === "S") return "S";
        if (r === "DRIVER" || r === "D") return "D";
        if (r === "ENGINEER" || r === "EN") return "EN";
        return "E";
      };

      const mapStatusToCode = (statusName) => {
        if (!statusName) return "A";
        const s = statusName.toString().toUpperCase();
        return (s === "ACTIVE" || s === "A") ? "A" : "I";
      };

      const formData = new FormData();
      formData.append("ServiceNo", userDetails.ServiceNo);
      formData.append("Name", editFormData.Name);
      formData.append("MobileNo", editFormData.MobileNo);
      formData.append("role", mapRoleToCode(userDetails.role));
      formData.append("status", mapStatusToCode(userDetails.status));
      formData.append("password", editFormData.password);
      
      if (userDetails.file_path) {
        formData.append("file_path", userDetails.file_path);
      }

      const response = await fetch("https://aeroviewbackend.dockyardsoftware.com/User/Fileupload", {
        method: "POST",
        headers: { "auth-key": authKey },
        body: formData,
      });

      const data = await response.json();

      if (data.statusCode === 200) {
        toast.success("Profile updated");
        setIsEditing(false);
        dispatch(fetchUserDetails(userDetails.ServiceNo));
      } else {
        toast.error("Update failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };

  // Stats data with colors
  const stats = [
    {
      label: "Total Users",
      value: totalUsers,
      icon: Users,
      change: "+12%",
      trend: "up",
      colorConfig: statsConfig.totalUsers
    },
    {
      label: "Active Users",
      value: activeUsers,
      icon: Activity,
      change: "+8%",
      trend: "up",
      colorConfig: statsConfig.activeUsers
    },
    {
      label: "Admin Accounts",
      value: adminAccounts,
      icon: UserCog,
      change: "+3%",
      trend: "up",
      colorConfig: statsConfig.adminAccounts
    },
    {
      label: "Inactive Users",
      value: inactiveUsers,
      icon: UserMinus,
      change: "-5%",
      trend: "down",
      colorConfig: statsConfig.inactiveUsers
    },
  ];

  // Quick actions
  const actions = [
    { icon: Truck, label: "Assign Vehicle", path: "/admin/vehicle-assign", color: "emerald" },
    { icon: MapPin, label: "Live Map", path: "/admin/map", color: "blue" },
    { icon: UserPlus, label: "Add User", path: "/admin/add-user", color: "purple" },
    { icon: FileText, label: "Reports", path: "/admin/reports", color: "amber" },
    { icon: Car, label: "Vehicles", path: "/admin/vehicles", color: "cyan" },
    { icon: ClipboardList, label: "Bookings", path: "/admin/bookings", color: "indigo" },
  ];

  // Recent users
  const recentUsers = users.slice(0, 5);

  return (
    <AdminLayout>
      {/* Header Section */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
          Dashboard
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          {' • '}
          {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`${stat.colorConfig.bg} border ${stat.colorConfig.border} rounded-lg p-3 sm:p-5 transition-all hover:shadow-sm`}
          >
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className={`${stat.colorConfig.iconBg} p-1.5 sm:p-2 rounded-lg`}>
                <stat.icon size={16} className={stat.colorConfig.iconColor + " sm:hidden"} />
                <stat.icon size={18} className={stat.colorConfig.iconColor + " hidden sm:block"} />
              </div>
              <span className={`text-xs font-medium ${
                stat.trend === 'up' ? 'text-emerald-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="text-xs text-gray-600 mt-0.5 sm:mt-1 font-medium">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Reduced Size Hero Section - Primary Action */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl overflow-hidden shadow-md">
          <div className="p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Left Content */}
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Primary Action</span>
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
                  Vehicle Assignment
                </h2>
                <p className="text-gray-300 text-xs sm:text-sm max-w-md">
                  Manage driver assignments, vehicle status & fleet operations
                </p>
                <button
                  onClick={() => navigate("/admin/vehicle-assign")}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm rounded-lg transition shadow-md"
                >
                  <Truck size={14} />
                  <span>Assign Vehicle</span>
                  <ChevronRight size={12} />
                </button>
              </div>
              
              {/* Right Icon */}
              <div className="flex-shrink-0">
                <div className="bg-white/10 backdrop-blur-sm p-2.5 sm:p-3 rounded-xl border border-white/20">
                  <Truck size={28} className="text-white sm:w-8 sm:h-8" strokeWidth={1.5} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          {actions.map((action, index) => {
            const colorClasses = {
              emerald: "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100",
              blue: "bg-blue-50 text-blue-600 group-hover:bg-blue-100",
              purple: "bg-purple-50 text-purple-600 group-hover:bg-purple-100",
              amber: "bg-amber-50 text-amber-600 group-hover:bg-amber-100",
              cyan: "bg-cyan-50 text-cyan-600 group-hover:bg-cyan-100",
              indigo: "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100",
            };
            return (
              <button
                key={index}
                onClick={() => navigate(action.path)}
                className="flex flex-col items-center gap-2 bg-white border border-gray-100 rounded-lg p-3 sm:p-4 hover:border-gray-200 hover:shadow-sm transition group"
              >
                <div className={`p-2 rounded-lg transition ${colorClasses[action.color]}`}>
                  <action.icon size={18} />
                </div>
                <span className="text-xs font-medium text-gray-700">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm">
            <div className="p-4 sm:p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-900">Profile</h2>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-1 hover:bg-gray-50 rounded transition"
                  >
                    <Edit2 size={14} className="text-gray-400" />
                  </button>
                ) : (
                  <div className="flex gap-1">
                    <button onClick={() => setIsEditing(false)} className="p-1 hover:bg-gray-50 rounded">
                      <X size={14} className="text-gray-400" />
                    </button>
                    <button onClick={handleUpdateProfile} className="p-1 hover:bg-gray-50 rounded">
                      <Save size={14} className="text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-4 sm:p-5">
              {userDetails ? (
                <div className="space-y-3 sm:space-y-4">
                  {/* Avatar */}
                  <div className="flex justify-center">
                    <div className="relative">
                      <img
                        src={profileImageUrl}
                        alt=""
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-100"
                      />
                      <button
                        onClick={() => fileInputRef.current.click()}
                        className="absolute bottom-0 right-0 p-1 bg-gray-900 rounded-full hover:bg-gray-800 transition"
                      >
                        <Camera size={8} className="text-white sm:hidden" />
                        <Camera size={10} className="text-white hidden sm:block" />
                      </button>
                      <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileSelect} className="hidden" />
                    </div>
                  </div>

                  {/* Details */}
                  <div>
                    <label className="text-xs text-gray-500">Service No</label>
                    <p className="text-xs sm:text-sm font-mono text-gray-900 mt-0.5 break-all">{userDetails.ServiceNo}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="Name"
                        value={editFormData.Name}
                        onChange={handleEditChange}
                        className="w-full mt-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                      />
                    ) : (
                      <p className="text-xs sm:text-sm text-gray-900 mt-0.5 break-words">{userDetails.Name}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Mobile</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="MobileNo"
                        value={editFormData.MobileNo}
                        onChange={handleEditChange}
                        className="w-full mt-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                      />
                    ) : (
                      <p className="text-xs sm:text-sm text-gray-900 mt-0.5">{userDetails.MobileNo}</p>
                    )}
                  </div>

                  {isEditing && (
                    <div>
                      <label className="text-xs text-gray-500">Password</label>
                      <div className="relative mt-1">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={editFormData.password}
                          onChange={handleEditChange}
                          className="w-full px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 pr-7 sm:pr-8"
                          placeholder="••••••••"
                        />
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                        >
                          {showPassword ? <EyeOff size={12} className="text-gray-400 sm:hidden" /> : <Eye size={12} className="text-gray-400 sm:hidden" />}
                          {showPassword ? <EyeOff size={14} className="text-gray-400 hidden sm:block" /> : <Eye size={14} className="text-gray-400 hidden sm:block" />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {userDetails.role}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      userDetails.status === "Active" 
                        ? "bg-emerald-50 text-emerald-700" 
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      {userDetails.status}
                    </span>
                  </div>

                  {profileImageBlob && (
                    <button
                      onClick={handleSaveProfileImage}
                      className="w-full mt-2 py-1.5 bg-gray-900 text-white text-xs sm:text-sm rounded hover:bg-gray-800 transition"
                    >
                      Save Image
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <Activity size={20} className="mx-auto text-gray-300 animate-spin sm:hidden" />
                  <Activity size={24} className="mx-auto text-gray-300 animate-spin hidden sm:block" />
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">Loading...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity and System Info */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Recent Activity */}
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm">
            <div className="p-4 sm:p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-900">Recent Activity</h2>
                <button 
                  onClick={() => navigate("/admin/users")}
                  className="text-xs text-gray-500 hover:text-gray-700 transition flex items-center gap-1"
                >
                  View all
                  <ChevronRight size={12} />
                </button>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {loading ? (
                <div className="p-6 sm:p-8 text-center">
                  <Activity size={16} className="mx-auto text-gray-300 animate-spin sm:hidden" />
                  <Activity size={20} className="mx-auto text-gray-300 animate-spin hidden sm:block" />
                </div>
              ) : recentUsers.length > 0 ? (
                recentUsers.map((user, index) => (
                  <div key={index} className="p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50/50 transition">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <img
                        src={`https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${user.ServiceNo}`}
                        alt=""
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.Name || "User")}&background=9CA3AF&color=fff&size=32`;
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{user.Name}</p>
                        <p className="text-xs text-gray-500">{user.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                      <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded ${
                        user.status === "Active" 
                          ? "bg-emerald-50 text-emerald-700" 
                          : "bg-gray-100 text-gray-500"
                      }`}>
                        {user.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 sm:p-8 text-center">
                  <Users size={20} className="mx-auto text-gray-300" />
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">No users found</p>
                </div>
              )}
            </div>
          </div>

          {/* System Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* System Information */}
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm">
              <div className="p-4 sm:p-5 border-b border-gray-100">
                <h2 className="text-sm font-medium text-gray-900">System Information</h2>
              </div>
              <div className="p-4 sm:p-5 space-y-2 sm:space-y-3">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-500">Status</span>
                  <span className="text-emerald-600 font-medium">Operational</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-500">Version</span>
                  <span className="text-gray-900">2.4.0</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-500">Last Backup</span>
                  <span className="text-gray-900">Today, 02:34 AM</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-500">Uptime</span>
                  <span className="text-gray-900">99.98%</span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm">
              <div className="p-4 sm:p-5 border-b border-gray-100">
                <h2 className="text-sm font-medium text-gray-900">Platform Stats</h2>
              </div>
              <div className="p-4 sm:p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-emerald-50 rounded">
                      <Shield size={14} className="text-emerald-600" />
                    </div>
                    <span className="text-xs text-gray-600">Active Rate</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {totalUsers ? Math.round((activeUsers / totalUsers) * 100) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div 
                    className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${totalUsers ? (activeUsers / totalUsers) * 100 : 0}%` }}
                  />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-50 rounded">
                      <Users size={14} className="text-purple-600" />
                    </div>
                    <span className="text-xs text-gray-600">Admin Ratio</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {totalUsers ? Math.round((adminAccounts / totalUsers) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Management Links */}
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm">
            <div className="p-4 sm:p-5 border-b border-gray-100">
              <h2 className="text-sm font-medium text-gray-900">Management</h2>
            </div>
            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <button
                  onClick={() => navigate("/admin/users")}
                  className="flex items-center justify-center sm:justify-start gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-xs sm:text-sm text-gray-700"
                >
                  <Users size={14} />
                  <span className="hidden sm:inline">Users</span>
                  <span className="sm:hidden">Users</span>
                </button>
                <button
                  onClick={() => navigate("/admin/vehicles")}
                  className="flex items-center justify-center sm:justify-start gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-xs sm:text-sm text-gray-700"
                >
                  <Car size={14} />
                  <span className="hidden sm:inline">Vehicles</span>
                  <span className="sm:hidden">Vehicles</span>
                </button>
                <button
                  onClick={() => navigate("/admin/bookings")}
                  className="flex items-center justify-center sm:justify-start gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-xs sm:text-sm text-gray-700"
                >
                  <ClipboardList size={14} />
                  <span className="hidden sm:inline">Bookings</span>
                  <span className="sm:hidden">Bookings</span>
                </button>
                <button
                  onClick={() => navigate("/admin/settings")}
                  className="flex items-center justify-center sm:justify-start gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-xs sm:text-sm text-gray-700"
                >
                  <Settings size={14} />
                  <span className="hidden sm:inline">Settings</span>
                  <span className="sm:hidden">Settings</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-4">
          <div className="w-full max-w-md bg-white rounded-lg overflow-hidden mx-3 sm:mx-4">
            <div className="p-3 sm:p-4 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-900">Crop Image</h3>
            </div>
            <div style={{ position: "relative", width: "100%", height: 280 }} className="sm:h-80">
              <Cropper
                image={previewImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="p-3 sm:p-4 border-t border-gray-100">
              <Slider value={zoom} min={1} max={3} step={0.1} onChange={(e, v) => setZoom(v)} />
              <div className="flex justify-between gap-2 mt-3 sm:mt-4">
                <button onClick={cancelCrop} className="flex-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-200 rounded hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button onClick={applyCrop} className="flex-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-gray-900 text-white rounded hover:bg-gray-800 transition">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
